import React, { useState, useEffect } from "react";

//import localization
import { useTranslation } from "react-i18next";

//Locales of Ant Design
import enGB from "antd/lib/locale/en_GB";
import elGR from "antd/lib/locale/el_GR";
import ruRU from "antd/lib/locale/ru_RU";

import dayjs from "dayjs";
//Locales of dayjs
import "dayjs/locale/en";
import "dayjs/locale/el";
import "dayjs/locale/ru";

import { Card, Grid, Row, Col, Space, Layout, PageHeader, Steps, Statistic, Button, ConfigProvider } from "antd";
import { LoadingOutlined, CloseOutlined, CalendarOutlined, SolutionOutlined, FileDoneOutlined, CreditCardOutlined, ArrowLeftOutlined, ProfileOutlined, PhoneOutlined, MailOutlined, TwitterOutlined, YoutubeFilled, InstagramFilled } from "@ant-design/icons";

import http from "../utils/http";

import FacebookIcon from "../images/facebook-brands.svg";
import LinkedIcon from "../images/linkedin-brands.svg";

import NavBarView from "./NavBarView";
import TripDetailsForm from "./TripDetailsForm";
import PlanView from "./PlanView";
import TravellersForm from "./TravellersForm";
import SummaryView from "./SummaryView";
import PaymentView from "./PaymentView";
import CancelForm from "./CancelForm";

//import { meta } from "./data";

export const stateContext = React.createContext();

const { useBreakpoint } = Grid;
const { Content, Footer } = Layout;

const { Step } = Steps;

function MainView({ query }) {
	//Locale Hooks
	const { t, i18n } = useTranslation();
	const [localeAntD, setLocaleAntD] = useState(enGB);

	//Controller for window resizing.
	//Used in: MainView -> Footer
	const screen = useBreakpoint();

	//metaValues: Holds the meta values that come at the initialization of the app
	//Used in: TripDetailsForm, PlanView, TravellersForm, SummaryView
	const [metaValues, setMetaValues] = useState({ Products: [], Destinations: [], Departures: [], CertificationTypes: [], Countries: [], Plans: [] });
	//formState: holds the current state from its component from start to end.
	//Used in: stateContext
	const [formState, setFormState] = useState({ CurrentStep: 0, ProductForm: {}, SelectedPlan: {}, TravellersForm: {}, AcceptOffer: {} });

	const [cancelState, setCancelState] = useState(false);

	useEffect(() => {
		// On initialization set locale
		switch (query.Language) {
			case "en-GB":
				i18n.changeLanguage("en");
				setLocaleAntD(enGB);
				dayjs.locale("en");
				break;
			case "el-GR":
				i18n.changeLanguage("el");
				setLocaleAntD(elGR);
				dayjs.locale("el");
				break;
			case "ru-RU":
				i18n.changeLanguage("ru");
				setLocaleAntD(ruRU);
				dayjs.locale("ru");
				break;
			default:
				break;
		}

		//sets metValues at initialization
		const getMeta = () => {
			http
				.get(`/travelinsurancesingle/meta`, {
					params: {
						ch: query.Channel,
						lg: query.Language
					}
				})
				.then((response) => {
					const data = response.data;

					setMetaValues(data);
				})
				.catch((error) => {
					getMeta();
				});
		};

		const getOfferDetails = () => {
			setMetaValues({ Products: [{ name: "Empty" }] });
			// console.log(query.Cancel, query.OfferId);
		};

		// setMetaValues(meta);
		if (query.Channel) {
			getMeta();
		} else if (query.Cancel && query.OfferId) {
			setCancelState(true);
			getOfferDetails();
		} else {
			console.log("Error");
		}
	}, [i18n, query]);

	//Holds each view of the application
	const steps = [
		{
			key: 0,
			title: <div style={{ color: "white" }}>{t("Trip")}</div>,
			icon: <CalendarOutlined style={{ color: formState.CurrentStep < 0 ? "white" : "#ed1b24" }} />,
			component: <TripDetailsForm metaValues={metaValues} query={query} setMetaValues={setMetaValues} localeAntD={localeAntD} />
		},
		{
			key: 1,
			title: <div style={{ color: "white" }}>{t("Plan")}</div>,
			icon: <ProfileOutlined rotate={90} style={{ color: formState.CurrentStep < 1 ? "white" : "#ed1b24" }} />,
			component: <PlanView plans={metaValues.Plans} query={query} />
		},
		{
			key: 2,
			title: <div style={{ color: "white" }}>{t("Travellers")}</div>,
			icon: <SolutionOutlined style={{ color: formState.CurrentStep < 2 ? "white" : "#ed1b24" }} />,
			component: <TravellersForm metaValues={metaValues} localeAntD={localeAntD} query={query} />
		},
		{
			key: 3,
			title: <div style={{ color: "white" }}>{t("Summary")}</div>,
			icon: <FileDoneOutlined style={{ color: formState.CurrentStep < 3 ? "white" : "#ed1b24" }} />,
			component: <SummaryView metaValues={metaValues} query={query} />
		},
		{
			key: 4,
			title: <div style={{ color: "white" }}>{t("Payment")}</div>,
			icon: <CreditCardOutlined style={{ color: formState.CurrentStep < 4 ? "white" : "#ed1b24" }} />,
			component: <PaymentView query={query} />
		}
	];

	//Loading state that waits for Products to be returned
	if (metaValues?.Products?.length === 0)
		return (
			<div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
				<Card style={{ width: 300, boxShadow: "0px 20px 8px -8px rgba(0, 0, 0, 0.1)" }}>
					<Row justify="center">
						<Col span={24} style={{ textAlign: "center", paddingBottom: 15 }}>
							{query.Channel ? <LoadingOutlined style={{ fontSize: 36, color: "#d8061f" }} /> : <CloseOutlined style={{ fontSize: 36, color: "#d8061f" }} />}
						</Col>
						<Col span={24} style={{ textAlign: "center" }}>
							{query.Channel ? <h3>{t("PleaseWait")}</h3> : <h3>No channel was given.</h3>}
						</Col>
					</Row>
				</Card>
			</div>
		);
	else
		return (
			<ConfigProvider locale={localeAntD}>
				<stateContext.Provider value={[formState, setFormState]}>
					<Layout className="App image-background">
						<NavBarView currentStep={formState.CurrentStep} query={query} />
						<Content>
							{!query.Cancel ? (
								<>
									<h1 className="travel-insurance">{t("TravelInsurance")}</h1>
									<Steps current={formState.CurrentStep} style={{ padding: screen.xs || (screen.sm && !screen.md) ? "20px 10px 0px 10px" : null }}>
										{steps.map((item) => (
											<Step key={item.key} title={item.title} icon={item.icon} />
										))}
									</Steps>
								</>
							) : null}
							<br />
							<div className="site-layout-background" style={{ maxWidth: formState.CurrentStep === 1 ? 1200 : null }}>
								{/* Header rendered from PlanView -> SummaryView */}
								{formState.CurrentStep > 0 && formState.CurrentStep < 4 ? (
									<PageHeader
										className="page-header"
										backIcon={
											<div style={{ fontSize: 17, fontWeight: "bold", display: "flex" }}>
												<ArrowLeftOutlined style={{ color: "white", paddingRight: 5 }} /> {screen.xs ? steps[formState.CurrentStep].title : null}
											</div>
										}
										onBack={() => {
											setFormState((formState) => {
												return { ...formState, CurrentStep: formState.CurrentStep - 1 };
											});
										}}
										extra={formState?.SelectedPlan?.price ? <Statistic valueStyle={{ color: "white" }} value={formState?.SelectedPlan?.MandatoryGrossAmount} prefix="€" /> : <div style={{ height: 37 }}> </div>}
									/>
								) : null}
								{/* According to the currentStep the equal view is being rendered. */}
								{!cancelState ? steps[formState.CurrentStep].component : <CancelForm query={query} />}
							</div>
						</Content>

						<Footer style={{ textAlign: "center", background: "#3f4b5b", color: "white" }}>
							{/* EmDoT ©2021 Travel Insurance */}

							<Row justify={screen.xs ? "start" : "center"} gutter={24}>
								<Col className="footer-cols">
									<a href={`https://www.interamerican.gr/${query.Language === "el-GR" ? "" : "en"}`} target="_blank" rel="noreferrer" style={{ color: "#fff" }}>
										Interamerican
									</a>
									<br />
									<a href={`https://www.interamerican.gr/${query.Language === "el-GR" ? "proswpika-dedomena" : "personal-data"}`} target="_blank" rel="noreferrer" style={{ color: "#fff" }}>
										{t("PersonalData")}
									</a>
									<br />
								</Col>
								<Col className="footer-cols">
									<div style={{ display: "flex", alignItems: "center", paddingBottom: 15 }}>
										<PhoneOutlined style={{ fontSize: 24 }} rotate={135} />{" "}
										<a style={{ paddingLeft: 5, paddingBottom: 3, color: "#fff" }} href={`tel:2109461999`}>
											210 946 1999
										</a>
									</div>
									<div style={{ display: "flex", alignItems: "center" }}>
										<MailOutlined style={{ fontSize: 24 }} />{" "}
										<a style={{ paddingLeft: 5, paddingBottom: 3, color: "#fff" }} href={`mailto:assistance@interamerican.gr`}>
											{" "}
											assistance@interamerican.gr
										</a>
									</div>
								</Col>
								<Col className="footer-cols">
									<Space size={4}>
										<Button
											className="footer-button"
											shape="circle"
											type="text"
											icon={<img src={FacebookIcon} alt="*" width={15} className="invert-button-image-color" />}
											onClick={() => {
												window.open("https://www.facebook.com/Interamerican/", "_blank");
											}}
										/>
										<Button
											className="footer-button"
											shape="circle"
											type="text"
											icon={<TwitterOutlined className="footer-button-icon" />}
											onClick={() => {
												window.open("https://twitter.com/interamericangr", "_blank");
											}}
										/>
										<Button
											className="footer-button"
											shape="circle"
											type="text"
											icon={<YoutubeFilled className="footer-button-icon" />}
											onClick={() => {
												window.open("https://www.youtube.com/user/INTERAMERICANGR", "_blank");
											}}
										/>
										<Button
											className="footer-button"
											shape="circle"
											type="text"
											icon={<img src={LinkedIcon} alt="*" width={20} className="invert-button-image-color" />}
											onClick={() => {
												window.open("https://www.linkedin.com/company/interamerican", "_blank");
											}}
										/>
										<Button
											className="footer-button"
											shape="circle"
											type="text"
											icon={<InstagramFilled className="footer-button-icon" />}
											onClick={() => {
												window.open("https://www.instagram.com/interamericangr/", "_blank");
											}}
										/>
									</Space>
								</Col>
							</Row>
						</Footer>
					</Layout>
				</stateContext.Provider>
			</ConfigProvider>
		);
}

export default React.memo(MainView);
